import { addDoc, deleteDoc, getDocs, getDoc, query, where, runTransaction } from 'firebase/firestore';
import { db, collectionRef, docRef } from '../firebase/config';
import {
  COLLECTIONS, INSTRUCTOR_STATUS,
  STUDENT_STATUS, USER_ROLE, STORAGE_KEYS, INSTRUCTOR_REGISTRATION_ENABLED,
} from '../utils/constants';
import { generateUserCode } from '../utils/stringUtils';
import { normalizeEmail, emailsMatch } from '../utils/emailUtils';
import { normalizePhoneTR } from '../utils/phoneUtils';
import {
  extractIdentityValues,
  syncIdentitiesInTx,
  releaseIdentities,
} from './identityUniqueness';

const PENDING_REG_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function savePendingInstructorRegistration({ name, email, phone }) {
  const trimmedName = String(name || '').trim();
  const normalizedEmail = email ? normalizeEmail(email) : '';
  const e164 = phone ? normalizePhoneTR(phone) : '';
  if (!trimmedName || (!normalizedEmail && !e164)) {
    throw new Error('INVALID_REGISTRATION');
  }
  window.localStorage.setItem(
    STORAGE_KEYS.PENDING_INSTRUCTOR_REG,
    JSON.stringify({
      name: trimmedName,
      email: normalizedEmail || null,
      phone: e164 || null,
      at: Date.now(),
    })
  );
}

export function peekPendingInstructorRegistration() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.PENDING_INSTRUCTOR_REG);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.name || !parsed?.at) return null;
    if (!parsed.email && !parsed.phone) return null;
    if (Date.now() - Number(parsed.at) > PENDING_REG_MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEYS.PENDING_INSTRUCTOR_REG);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingInstructorRegistration() {
  window.localStorage.removeItem(STORAGE_KEYS.PENDING_INSTRUCTOR_REG);
}

const pendingMatchesUser = (pending, user) => {
  if (!pending || !user) return false;
  if (pending.email && user.email && emailsMatch(pending.email, user.email)) return true;
  if (pending.phone && user.phoneNumber) {
    return pending.phone === user.phoneNumber || normalizePhoneTR(pending.phone) === user.phoneNumber;
  }
  return false;
};

/** Auth sonrası bekleyen eğitmen kaydını uygular (e-posta veya telefon); yoksa null */
export async function completePendingInstructorRegistration(user) {
  if (!INSTRUCTOR_REGISTRATION_ENABLED || !user) return null;
  if (!user.email && !user.phoneNumber) return null;
  const pending = peekPendingInstructorRegistration();
  if (!pendingMatchesUser(pending, user)) return null;
  await registerUserProfile({
    user,
    authRole: USER_ROLE.INSTRUCTOR,
    name: pending.name,
  });
  clearPendingInstructorRegistration();
  return true;
}

export async function registerUserProfile({ user, authRole, name }) {
  const col = authRole === USER_ROLE.INSTRUCTOR ? COLLECTIONS.INSTRUCTORS : COLLECTIONS.STUDENTS;
  const consentTimestamp = Date.now();
  const phoneE164 = user.phoneNumber
    ? (normalizePhoneTR(user.phoneNumber) || user.phoneNumber)
    : '';
  const identity = user.email
    ? normalizeEmail(user.email)
    : phoneE164;
  if (!identity) {
    throw new Error('INVALID_REGISTRATION');
  }

  const profileData = {
    name,
    emailOrPhone: identity,
    role: authRole,
    status: authRole === USER_ROLE.INSTRUCTOR ? INSTRUCTOR_STATUS.ACTIVE : STUDENT_STATUS.ACTIVE,
    userCode: generateUserCode(),
    consentGivenAt: consentTimestamp,
    consentVersion: '1.0',
    termsAccepted: true,
    ...(phoneE164 ? { phoneNumber: phoneE164 } : {}),
    ...(authRole === USER_ROLE.STUDENT && {
      instructorId: null,
      instructorEmail: null,
      age: 0,
      parent: '-',
    }),
  };

  await runTransaction(db, async (tx) => {
    await syncIdentitiesInTx(tx, user.uid, {
      claim: extractIdentityValues(profileData),
    });
    tx.set(docRef(col, user.uid), profileData);
  });

  const consentBase = {
    userId: user.uid,
    email: identity,
    role: authRole,
    consentVersion: '1.0',
    consentAt: consentTimestamp,
    platform: 'web',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'test',
  };
  const consentsCol = collectionRef(COLLECTIONS.CONSENTS);
  try {
    await addDoc(consentsCol, { ...consentBase, consentType: 'terms' });
  } catch (consentErr) {
    // Profil oluştuysa rıza logu kaydı engellemesin
    console.warn('Rıza kaydı yazılamadı (profil oluşturuldu):', consentErr);
  }
}

/** Kayıt rollback — rıza kayıtları + Firestore profilini temizler */
export async function deleteUserProfile(userId, authRole) {
  try {
    const consentsSnap = await getDocs(
      query(collectionRef(COLLECTIONS.CONSENTS), where('userId', '==', userId))
    );
    await Promise.all(consentsSnap.docs.map((d) => deleteDoc(d.ref).catch(() => {})));
  } catch {
    /* consent temizliği başarısız olsa da profil silinmeye devam edilir */
  }

  const col = authRole === USER_ROLE.INSTRUCTOR ? COLLECTIONS.INSTRUCTORS : COLLECTIONS.STUDENTS;
  try {
    const snap = await getDoc(docRef(col, userId));
    if (snap.exists()) {
      await releaseIdentities(userId, extractIdentityValues(snap.data()));
    }
  } catch {
    /* kimlik serbest bırakılamasa da profil silinmeye devam edilir */
  }
  await deleteDoc(docRef(col, userId)).catch(() => {});
}
